import {
  PageHeader,
  Button,
  PasswordInput,
  Breadcrumbs,
  Loader,
  useNotify,
} from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { FormEvent, KeyboardEventHandler } from 'react';
import { useCallback, useState } from 'react';
import { PasswordInputWithValidation } from '@hotjar/webapp/components/PasswordInputWithValidation';
import { ErrorText } from '@hotjar/webapp/components/ErrorText';
import { useCurrentUser } from '@hotjar/user';
import { PROFILE } from '@hotjar/webapp/constants/routes';
import { useMutation } from 'react-query';
import { getRoute, navigateTo } from '@hotjar/webapp/utils/routing';
import * as Sentry from '@sentry/browser';
import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { Page, Wrapper, StyledField } from './styles';
import { mutateProfilePassword } from './query';
import { JSError } from './utils';

export function ProfileChangePassword(): JSX.Element | null {
  const { t } = useTranslation('profile', { keyPrefix: 'change-password' });
  const { t: tNavigation } = useTranslation('navigation');
  useWindowTitle(t('page-title'));
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>('');
  const [noMatch, setNoMatch] = useState<boolean>(false);
  // isSubmitAttempted is used to show field validation errors if validation didn't pass
  const [isSubmitAttempted, setIsSubmitAttempted] = useState<boolean>(false);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);

  const { isLoading: isLoadingUser, data: userData } = useCurrentUser();

  const { showToast } = useNotify();

  const handleJSError = useCallback(
    (error: unknown) => {
      if (error instanceof JSError) {
        showToast({
          kind: 'danger',
          text: t('generic-error'),
        });
        Sentry.captureException(error, {
          tags: {
            squad: 'security-and-auth',
            criticality: 'minor',
            featureName: 'profile.password',
          },
        });
      } else {
        showToast({
          kind: 'danger',
          text: t('generic-error'),
        });
      }
    },
    [t, showToast],
  );

  const { mutate, isLoading: isUpdatingPassword } = useMutation(mutateProfilePassword, {
    onSuccess(data) {
      if (data.success) {
        showToast({
          kind: 'success',
          text: t('password-successfully-updated'),
        });
        navigateTo(getRoute(PROFILE.DETAILS.path));
      } else {
        showToast({
          kind: 'danger',
          text: data.error.currentPassword[0],
        });
      }
    },
    onError: handleJSError,
  });

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();

      setIsSubmitAttempted(true);

      if (newPasswordConfirm !== newPassword) {
        setNoMatch(true);
        return;
      }

      if (typeof userData?.id !== 'number') {
        return;
      }

      mutate({
        userId: userData?.id,
        currentPassword,
        newPassword,
        newPasswordConfirm,
      });
    },
    [currentPassword, mutate, userData?.id, newPasswordConfirm, newPassword],
  );

  const handlePressEnter = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.key !== 'Enter') return;

      setIsSubmitAttempted(true);
      if (isPasswordValid) {
        handleSubmit();
      }
    },
    [handleSubmit, isPasswordValid],
  );

  if (isLoadingUser || !userData) {
    return <Loader size="xl" />;
  }

  return (
    <Page>
      <PageHeader
        title={t('page-title')}
        breadcrumbItems={[
          <Breadcrumbs.Item href={PROFILE.DETAILS.path}>
            {tNavigation('ProfileMenu.myDetails')}
          </Breadcrumbs.Item>,
          <Breadcrumbs.Item isCurrent>{t('page-title')}</Breadcrumbs.Item>,
        ]}
      />
      <Wrapper>
        <form onSubmit={handleSubmit}>
          <StyledField
            size="full"
            labelText={t(`current-password-input-label`)}
            inputId="current-password"
          >
            <PasswordInput
              id="current-password"
              name="current-password"
              data-testid="current-password"
              placeholder={t('current-password-input-placeholder')}
              size="m"
              onChange={(e) => {
                setCurrentPassword(e.target.value);
              }}
              onKeyPress={handlePressEnter}
            />
          </StyledField>
          <PasswordInputWithValidation
            id="new-password"
            name="new-password"
            label="profile:change-password.new-password-input-label"
            placeholder="profile:change-password.new-password-input-placeholder"
            required={isSubmitAttempted}
            isPasswordValidCallback={(isValid) => setIsPasswordValid(Boolean(isValid))}
            onChange={(val) => {
              setNewPassword(val);
              setNoMatch(false);
            }}
            onKeyPress={handlePressEnter}
            size="m"
          />
          <StyledField
            size="full"
            labelText={t(`confirm-new-password-input-label`)}
            inputId="confirm-new-password"
          >
            <PasswordInput
              id="confirm-new-password"
              name="confirm-new-password"
              data-testid="confirm-password"
              placeholder={t('confirm-new-password-input-placeholder')}
              size="m"
              onChange={(e) => {
                setNewPasswordConfirm(e.target.value);
                setNoMatch(false);
              }}
              isInvalid={noMatch || (isSubmitAttempted && !newPasswordConfirm)}
              onKeyPress={handlePressEnter}
            />
            {noMatch && (
              <ErrorText data-testid="no-match-error-message">{t('no-match-error')}</ErrorText>
            )}
          </StyledField>
          <Button
            id="submit"
            type="submit"
            data-testid="reset-password-btn"
            isDisabled={!newPassword || !newPasswordConfirm || !isPasswordValid}
            size="m"
            isLoading={isUpdatingPassword}
          >
            {t(`save`)}
          </Button>
        </form>
      </Wrapper>
    </Page>
  );
}
